import React from 'react';
import {
  FileText, BellRing, Mail, MapPin, Clock, ListOrdered, FileWarning,
} from 'lucide-react';

/**
 * Shared read-only schedule card for review surfaces (FECOA review workflow).
 *
 * Renders one schedule's full saved configuration: arms, surveys, reminders,
 * missing forms, the FECOA-1236 delivery model (day-offset window + intra-day
 * timepoints with their per-timepoint surveys and completion-order flag), and
 * stages. Used by both the full-page StudyDesignReview and the Study Drafts
 * detail modal so the two review surfaces can't drift apart.
 */

// FECOA-1236: the Delivery Window is described by a day offset relative to the
// expected assessment day (0 = expected day, negative = before, positive = after).
export const fmtDayOffset = (d) => {
  const n = parseInt(d, 10) || 0;
  if (n === 0) return 'Expected day';
  if (n < 0) return `${Math.abs(n)} day${Math.abs(n) !== 1 ? 's' : ''} before`;
  return `${n} day${n !== 1 ? 's' : ''} after`;
};

// Accept "HH:MM" or "HH:MM:SS"; render as "9:00 AM".
export const fmtTargetTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':').map((v) => parseInt(v, 10));
  if (Number.isNaN(h)) return t;
  const ampm = h >= 12 ? 'PM' : 'AM';
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${String(m || 0).padStart(2, '0')} ${ampm}`;
};

const convertUTCToLocal = (timeObj) => {
  if (!timeObj || timeObj.period !== 'UTC') return timeObj;
  const utcDate = new Date();
  utcDate.setUTCHours(parseInt(timeObj.hours, 10), parseInt(timeObj.minutes, 10), 0, 0);
  let localHours = utcDate.getHours();
  const localPeriod = localHours >= 12 ? 'PM' : 'AM';
  localHours = localHours % 12;
  if (localHours === 0) localHours = 12;
  return {
    hours: localHours.toString().padStart(2, '0'),
    minutes: utcDate.getMinutes().toString().padStart(2, '0'),
    period: localPeriod,
  };
};

// Legacy pre-FECOA-1236 window times were stored UTC; show the viewer's local
// time WITH a timezone label, then the canonical UTC value, so it's never
// ambiguous which timezone is meant, e.g. "08:00 PM EDT · 00:00 UTC".
export const fmtLegacyWindowTime = (timeObj) => {
  if (!timeObj) return '—';
  if (timeObj.period !== 'UTC') {
    return `${timeObj.hours}:${timeObj.minutes} ${timeObj.period || ''}`.trim();
  }
  const local = convertUTCToLocal(timeObj);
  let tz = 'local';
  try {
    tz = new Intl.DateTimeFormat('en-US', { timeZoneName: 'short' })
      .formatToParts(new Date())
      .find((p) => p.type === 'timeZoneName')?.value || 'local';
  } catch { /* keep the 'local' fallback */ }
  const utc = `${String(timeObj.hours).padStart(2, '0')}:${String(timeObj.minutes).padStart(2, '0')}`;
  return `${local.hours}:${local.minutes} ${local.period} ${tz} · ${utc} UTC`;
};

export const SectionLabel = ({ children }) => (
  <p className="mb-2 text-[12.5px] font-medium text-gray-500 dark:text-gray-400">{children}</p>
);

// Soft chips. Arms are categories (gray); the enforced-order flag reads as info.
export const Chip = ({ children, tone = 'primary' }) => {
  const tones = {
    primary: 'badge-gray',
    gray: 'badge-gray',
    amber: 'badge-info',
  };
  return (
    <span className={`badge ${tones[tone] || tones.primary}`}>
      {children}
    </span>
  );
};

export const Muted = ({ children }) => (
  <p className="text-[13px] text-gray-500 dark:text-gray-400">{children}</p>
);

// Shared chip so enforcement reads identically in both delivery modes.
export const EnforcedOrderChip = () => (
  <Chip tone="amber">
    <ListOrdered className="h-3 w-3" /> Completion order enforced
  </Chip>
);

// One intra-day timepoint row: name, target time + window length, the surveys
// delivered at that timepoint, and the completion-order flag (FECOA-1333).
const TimepointRow = ({ tp, surveyName, allSurveyIds }) => {
  const tpSurveys = Array.isArray(tp.surveys) ? tp.surveys : [];
  // When order is enforced the sequence IS the setting, so the read-only view has
  // to number it the way the builder does. An unnumbered list told a reviewer that
  // enforcement was on but not what it enforced (FECOA-1948).
  const enforced = !!tp.enforce_order;
  const deliversAll =
    allSurveyIds.length > 0 &&
    tpSurveys.length >= allSurveyIds.length &&
    allSurveyIds.every((id) => tpSurveys.some((sid) => String(sid) === String(id)));

  return (
    <div className="py-2.5 first:pt-0 last:pb-0">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
        <span className="text-[13px] font-medium text-gray-900 dark:text-gray-50">{tp.name}</span>
        <span className="inline-flex items-center gap-1 text-xs tabular-nums text-gray-500 dark:text-gray-400">
          <Clock className="h-3 w-3" />
          {fmtTargetTime(tp.target_time)} · {tp.window_minutes} min window
        </span>
        {enforced && <EnforcedOrderChip />}
      </div>
      <ul className="mt-1.5 space-y-0.5">
        {tpSurveys.length === 0 && (
          <li className="text-xs text-gray-500 dark:text-gray-400">No surveys assigned</li>
        )}
        {tpSurveys.map((sid, i) => (
          <li key={sid} className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
            {enforced ? (
              <span className="flex-shrink-0 font-medium tabular-nums text-gray-400 dark:text-gray-500">{i + 1}.</span>
            ) : (
              <FileText className="h-3 w-3 flex-shrink-0 text-gray-400 dark:text-gray-500" />
            )}
            <span className="truncate">{surveyName(sid)}</span>
          </li>
        ))}
        {deliversAll && (
          <li className="text-[11px] text-gray-500 dark:text-gray-400">All schedule surveys</li>
        )}
      </ul>
    </div>
  );
};

const ScheduleReviewCard = ({ schedule, surveyName, reminderName }) => {
  const arms = Array.isArray(schedule.arms) ? schedule.arms : [];
  const surveys = (schedule.standardSurveys || []).map(surveyName);
  const missingForms = schedule.enableMissingForms ? (schedule.missingForms || []).map(surveyName) : [];
  const pushR = schedule.enablePushReminders ? (schedule.pushReminders || []).map(reminderName) : [];
  const emailR = schedule.enableEmailReminders ? (schedule.emailReminders || []).map(reminderName) : [];
  const windows = Array.isArray(schedule.assessmentWindows)
    ? schedule.assessmentWindows
    : schedule.assessmentWindow ? [schedule.assessmentWindow] : [];
  // FECOA-1236: a single shared Open/Close Day range per schedule.
  const dayWindow = windows[0] || null;
  // Once a day = no timepoints or a single unnamed one; multiple = named rows.
  const timepoints = Array.isArray(schedule.timepoints) ? schedule.timepoints : [];
  const isOnceDelivery = timepoints.length === 0 || (timepoints.length === 1 && !(timepoints[0].name || '').trim());
  const onceTp = timepoints[0];
  const isAllDayOnce = onceTp && onceTp.target_time === '00:00' && parseInt(onceTp.window_minutes, 10) === 1440;
  // Once-a-day enforcement was settable in the builder but invisible here, so a
  // reviewer had to create an amendment just to read the flag back (FECOA-1948).
  // Its sequence is the schedule's own survey list, which is what the builder's
  // once-a-day control points at.
  const onceEnforced = isOnceDelivery && !!onceTp?.enforce_order;
  const allSurveyIds = (schedule.standardSurveys || []).map(String);
  const stages = Array.isArray(schedule.stages) ? schedule.stages : [];

  return (
    <div className="card overflow-hidden">
      {/* Header */}
      <div className="flex items-center gap-3 border-b border-gray-100 px-5 py-4 dark:border-white/[0.06]">
        <div className="min-w-0 flex-1">
          <h3 className="truncate text-[15px] font-semibold text-gray-900 dark:text-gray-50">{schedule.name || 'Untitled schedule'}</h3>
          <p className="truncate text-[12.5px] text-gray-500 dark:text-gray-400">{schedule.type || 'No type set'}</p>
        </div>
        {schedule.siteType && (
          <Chip tone="gray">
            <MapPin className="h-3 w-3" />
            {schedule.siteType === 'on-site' ? 'On-site' : 'Remote'}
          </Chip>
        )}
      </div>

      {/* Body */}
      <div className="p-5 grid gap-5 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <SectionLabel>Arms</SectionLabel>
          {arms.length > 0
            ? <div className="flex flex-wrap gap-1.5">{arms.map((a, i) => <Chip key={i}>{a}</Chip>)}</div>
            : <Muted>No arms selected</Muted>}
        </div>

        <div className="sm:col-span-2">
          <div className="flex flex-wrap items-center gap-2">
            <SectionLabel>Standard Surveys</SectionLabel>
            {onceEnforced && <EnforcedOrderChip />}
          </div>
          {surveys.length > 0 ? (
            <ul className="space-y-1.5">
              {surveys.map((name, i) => (
                <li key={i} className="flex items-center gap-2 text-[13px] text-gray-700 dark:text-gray-200">
                  {onceEnforced ? (
                    <span className="flex-shrink-0 font-medium tabular-nums text-gray-400 dark:text-gray-500">{i + 1}.</span>
                  ) : (
                    <FileText className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" />
                  )}
                  {name}
                </li>
              ))}
            </ul>
          ) : <Muted>No surveys configured</Muted>}
          {onceEnforced && surveys.length > 0 && (
            <p className="mt-1.5 text-xs text-gray-500 dark:text-gray-400">
              Participants must complete these surveys in this order before the next one unlocks.
            </p>
          )}
        </div>

        <div className="sm:col-span-2">
          <SectionLabel>Delivery Window</SectionLabel>
          {dayWindow || timepoints.length > 0 ? (
            <div className="space-y-3 rounded-lg border border-gray-200/70 bg-gray-50/80 p-3.5 dark:border-white/[0.07] dark:bg-white/[0.03]">
              {dayWindow && (
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                  <div><span className="text-gray-500 dark:text-gray-400">Open day</span><span className="ml-1.5 font-medium text-gray-900 dark:text-gray-50">{fmtDayOffset(dayWindow.openWindowDay)}</span></div>
                  <div><span className="text-gray-500 dark:text-gray-400">Close day</span><span className="ml-1.5 font-medium text-gray-900 dark:text-gray-50">{fmtDayOffset(dayWindow.closeWindowDay)}</span></div>
                </div>
              )}
              {isOnceDelivery ? (
                <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-gray-700 dark:text-gray-200">
                  <span>
                  <span className="font-medium">Once a day</span>
                  {onceTp?.target_time ? (
                    isAllDayOnce
                      ? ' · available all day'
                      : ` · ${fmtTargetTime(onceTp.target_time)} · ${onceTp.window_minutes} min window`
                  ) : (
                    // Legacy pre-timepoint drafts stored intra-day clock times on
                    // the window itself; show them so old drafts still review true.
                    dayWindow?.startTime
                      ? ` · ${fmtLegacyWindowTime(dayWindow.startTime)} to ${fmtLegacyWindowTime(dayWindow.endTime)}`
                      : ''
                  )}
                  </span>
                  {onceEnforced && <EnforcedOrderChip />}
                </div>
              ) : (
                <div>
                  <p className="mb-2.5 text-xs font-medium text-gray-700 dark:text-gray-200">
                    Multiple times a day · {timepoints.length} timepoint{timepoints.length === 1 ? '' : 's'}
                  </p>
                  <div className="divide-y divide-gray-200/70 border-t border-gray-200/70 pt-2.5 dark:divide-white/[0.06] dark:border-white/[0.06]">
                    {timepoints.map((tp, i) => (
                      <TimepointRow key={tp.id || i} tp={tp} surveyName={surveyName} allSurveyIds={allSurveyIds} />
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : <Muted>No delivery window configured</Muted>}
        </div>

        <div className="sm:col-span-2">
          <SectionLabel>Missing Forms</SectionLabel>
          {missingForms.length > 0 ? (
            <ul className="space-y-1.5">
              {missingForms.map((n, i) => (
                <li key={i} className="flex items-center gap-2 text-[13px] text-gray-700 dark:text-gray-200">
                  <FileWarning className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" /> {n}
                </li>
              ))}
            </ul>
          ) : <Muted>Not enabled</Muted>}
        </div>

        <div>
          <SectionLabel>Push Reminders</SectionLabel>
          {pushR.length > 0 ? (
            <ul className="space-y-1.5">
              {pushR.map((n, i) => (
                <li key={i} className="flex items-center gap-2 text-[13px] text-gray-700 dark:text-gray-200">
                  <BellRing className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" /> {n}
                </li>
              ))}
            </ul>
          ) : <Muted>Not enabled</Muted>}
        </div>

        <div>
          <SectionLabel>Email Reminders</SectionLabel>
          {emailR.length > 0 ? (
            <ul className="space-y-1.5">
              {emailR.map((n, i) => (
                <li key={i} className="flex items-center gap-2 text-[13px] text-gray-700 dark:text-gray-200">
                  <Mail className="h-4 w-4 flex-shrink-0 text-gray-400 dark:text-gray-500" /> {n}
                </li>
              ))}
            </ul>
          ) : <Muted>Not enabled</Muted>}
        </div>

        <div className="sm:col-span-2">
          <SectionLabel>Stages</SectionLabel>
          {stages.length > 0 ? (
            <div className="divide-y divide-gray-200/70 rounded-lg border border-gray-200/70 bg-gray-50/80 dark:divide-white/[0.06] dark:border-white/[0.07] dark:bg-white/[0.03]">
              {stages.map((s, i) => (
                <div key={i} className="flex items-baseline gap-3 px-3.5 py-2.5">
                  <span className="w-4 flex-shrink-0 text-xs font-medium tabular-nums text-gray-400 dark:text-gray-500">{i + 1}</span>
                  <div className="min-w-0">
                    <div className="text-[13px] font-medium text-gray-900 dark:text-gray-50">{s.name}</div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">
                      Days {s.startDay}–{s.endDay}, repeats every {s.frequency?.value} {s.frequency?.unit}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : <Muted>No stages defined</Muted>}
        </div>
      </div>
    </div>
  );
};

export default ScheduleReviewCard;
